<template>
  <v-menu v-if="records.length > 0" v-model="open" :close-on-content-click="false" location="bottom start" min-width="760" max-width="1100">
    <template #activator="{ props: menuProps }">
      <button type="button" class="history-btn" v-bind="menuProps" :title="`${records.length} Änderung(en)`">
        <History class="history-icon" />
      </button>
    </template>

    <v-card elevation="2" class="history-card">
      <v-card-title class="history-card-title">{{ $gt('Feldhistorie') }}</v-card-title>
      <v-divider />
      <div class="history-table-wrapper">
        <table class="history-table">
          <thead>
            <tr>
              <th>{{ $gt('Datum') }}</th>
              <th>{{ $gt('Feld') }}</th>
              <th>{{ $gt('Rolle') }}</th>
              <th>{{ $gt('Unternehmen') }}</th>
              <th>{{ $gt('Benutzer') }}</th>
              <th>{{ $gt('Vorher') }}</th>
              <th>{{ $gt('Nachher') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(record, index) in sortedRecords" :key="record.id" :class="{ 'row-current': index === 0 }">
              <td class="td-date">
                {{ formatDate(record.changedAt) }}
                <span v-if="index === 0" class="badge-current">{{ $gt('Aktuell') }}</span>
                <!-- Kundenfeedback 21.09.2026: Herkunft automatisch
                     übernommener Werte (Gutachten, DAT) sichtbar machen. -->
                <span v-if="record.source" class="badge-source" :title="sourceLabel(record.source)">
                  <FileSearch class="badge-source-icon" />
                  {{ sourceLabel(record.source) }}
                </span>
              </td>
              <td class="td-value td-field">{{ formatFieldName(record.fieldName) }}</td>
              <td class="td-value td-role">{{ formatRoles(record.orgRoles) }}</td>
              <td class="td-value">{{ formatValue(record.orgName) }}</td>
              <td class="td-user">{{ formatUser(record) }}</td>

              <td class="td-value td-before">{{ formatValue(record.oldValue, record.fieldName) }}</td>
              <td class="td-value td-after">{{ formatValue(record.newValue, record.fieldName) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { History, FileSearch } from "lucide-vue-next";
import { CHANGE_SOURCE_LABELS } from "@/services/auditApi";
import { participantRolesLabel } from "@/utils/organizationRoles";

// Betragsfelder der Werte-Erfassung: werden in der Historie als Euro-Betrag
// formatiert statt als rohe Dezimalzahl.
const AMOUNT_FIELDS = new Set([
  "repairCostsEstimate", "repairCostsInvoice", "replacementValue", "residualValue",
  "restwertVersicherung", "impairment", "wertverbesserungBrutto", "abzugNeuFuerAltBrutto",
  "kostenNotreparaturBrutto", "amount",
]);
const PERCENT_FIELDS = new Set(["mwstSatzRk", "mwstSatzRkInvoice", "mwstSatzWbw", "mwstSatzRw", "mwstSatzNeufahrzeug"]);
const EUR = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

export default {
  name: "HistoryIndicator",

  components: { History, FileSearch },

  props: {
    records: {
      type: Array,
      default: () => [],
    },
    onRefresh: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      open: false,
    };
  },

  watch: {
    open(newVal) {
      if (newVal && typeof this.onRefresh === "function") {
        // Invalidate the cache for this row so fresh data is fetched when the
        // popup opens. This ensures the user always sees the latest saved state,
        // not a stale cached version from earlier in the session.
        this.onRefresh();
      }
    },
  },

  computed: {
    sortedRecords() {
      return [...this.records].sort((a, b) => b.changedAt.localeCompare(a.changedAt));
    },
  },

  methods: {
    formatFieldName(fieldName) {
      if (!fieldName) return "—";
      const labels = {
        includesVat: "Netto/Brutto",
        amount: "Betrag",
        invoiceDate: "Rechnungsdatum",
        dueDate: "Frist",
        billedParty: "Rechnung an",
        estimateSource: "Ursprung",
        note: "Notiz",
        idDocument: "Dokument",
        idEstimate: "Bezifferung",
        payedAt: "Am",
        payedBy: "Bezahlt von",
        payedTo: "Zahlung an",
        status: "Status",
        reason: "Grund",
        justification: "Begründung",
        paid: "Bezahlt",
        assignedClaim: "Anspruch abgetreten",
        positionDesc: "Position",
        positionMisc: "Zusatz",
        // Werte-Erfassung (Fahrzeug)
        repairCostsEstimate: "Rep.-Kosten lt. GA/KVA",
        repairCostsInvoice: "Rep-Kosten lt. Rechnung",
        replacementValue: "Wiederbeschaffungswert (WBW)",
        residualValue: "Restwert (RW)",
        restwertVersicherung: "Restwertangebot Versicherung",
        impairment: "Wertminderung",
        wertverbesserungBrutto: "Wertverbesserung",
        abzugNeuFuerAltBrutto: "Abzug neu für alt",
        wertminderungErmitteltNach: "Ermittelt nach",
        mwstSatzRk: "MwSt-Satz Rep.-Kosten (GA)",
        mwstSatzRkInvoice: "MwSt-Satz Rep.-Kosten (Rechnung)",
        mwstSatzWbw: "MwSt lt. GA (WBW)",
        mwstSatzRw: "MwSt-Satz Restwert",
        vat: "Vorsteuerabzug",
        besteuerung: "Besteuerung WBW",
        reparaturdauerArbeitstage: "Reparaturdauer (Arbeitstage)",
        basisMietwagenklasse: "Mietwagenklasse",
      };
      return labels[fieldName] || fieldName;
    },

    sourceLabel(source) {
      return CHANGE_SOURCE_LABELS[source] || source;
    },

    formatDate(changedAt) {
      if (!changedAt) return "—";
      // changedAt format: "2026-03-28T10:15:00"
      const [datePart, timePart] = changedAt.split("T");
      if (!datePart) return changedAt;
      const [y, m, d] = datePart.split("-");
      const time = timePart ? timePart.slice(0, 5) : "";
      return time ? `${d}.${m}.${y} ${time}` : `${d}.${m}.${y}`;
    },

    formatUser(record) {
      if (record.userFirstName || record.userLastName) {
        return `${record.userFirstName ?? ""} ${record.userLastName ?? ""}`.trim();
      }
      if (record.userInitials) return record.userInitials;
      return `#${record.idUser}`;
    },

    formatRoles(roles) {
      return participantRolesLabel(roles, "—");
    },

    formatValue(value, fieldName) {
      if (value === null || value === undefined) return "—";
      // Netto/Brutto-Schalter: "true"/"false" waere hier als "Ja"/"Nein"
      // irrefuehrend (Feedback VIII zur Feldhistorie).
      if (fieldName === "includesVat") {
        if (value === "true") return "Brutto";
        if (value === "false") return "Netto";
      }
      if (AMOUNT_FIELDS.has(fieldName) && Number.isFinite(Number(value))) {
        return EUR.format(Number(value));
      }
      if (PERCENT_FIELDS.has(fieldName) && Number.isFinite(Number(value))) {
        return `${String(Number(value)).replace(".", ",")} %`;
      }
      if (value === "yes") return "Ja";
      if (value === "no") return "Nein";
      if (value === "maybe") return "Unklar";
      if (value === "true") return "Ja";
      if (value === "false") return "Nein";
      if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        return this.formatDate(value);
      }
      if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
        return this.formatDate(value);
      }
      if (typeof value === "string" && /^\[.*\]$/.test(value)) {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed.join(", ");
          }
        } catch (e) {
          // Not a valid JSON, return as is
        }
      }
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      return value;
    },
  },
};
</script>

<style scoped>
.history-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  line-height: 0;
  flex-shrink: 0;
}

.history-icon {
  width: 14px;
  height: 14px;
  stroke-width: 1.5;
  color: var(--primary-color);
  opacity: 0.7;
  transition: opacity 0.15s;
}

.history-btn:hover .history-icon {
  opacity: 1;
}

.history-card {
  border-radius: 8px;
}

.history-card-title {
  font-size: 0.95rem;
  font-weight: 500;
  padding: 10px 16px;
  color: var(--dark-color);
}

.history-table-wrapper {
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.history-table th {
  text-align: left;
  padding: 6px 12px;
  font-weight: 600;
  color: var(--grey-color);
  border-bottom: 1px solid var(--border-grey-color);
  white-space: nowrap;
  position: sticky;
  top: 0;
  background: white;
}

.history-table td {
  padding: 6px 12px;
  border-bottom: 1px solid var(--border-grey-color);
  vertical-align: top;
  color: var(--dark-color);
}

.history-table tr:last-child td {
  border-bottom: none;
}

.td-user {
  white-space: nowrap;
}

.td-value {
  max-width: 180px;
  word-break: break-word;
}
.td-field {
  font-weight: 600;
  white-space: nowrap;
}
.td-role {
  white-space: nowrap;
}
.history-table td.td-before {
  color: var(--error-color);
}
.history-table td.td-after {
  color: var(--success-color);
}

.row-current {
  background-color: rgba(var(--primary-color-rgb, 22, 119, 255), 0.04);
}

.badge-current {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  background-color: var(--primary-color);
  color: white;
  vertical-align: middle;
  line-height: 1.4;
}

.td-date {
  white-space: nowrap;
}

.badge-source {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
  vertical-align: middle;
  line-height: 1.4;
}

.badge-source-icon {
  width: 11px;
  height: 11px;
  stroke-width: 2;
}
</style>
